import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../i18n/useP';
import { listPlAccounts, listLines, patchPlAccountLine, createPlAccount, deletePlAccounts } from '../api/financials_pl';
import PageHeader from '../components/PageHeader';
import CardShell from '../components/CardShell';
import '../components/EditableCell.css';
import './MappingPage.css';

function LineSelect({ groups, value, onChange }) {
  const p = useP();
  return (
    <select
      className="mx-line-select"
      value={value}
      onChange={e => onChange(e.target.value)}
      onClick={e => e.stopPropagation()}
    >
      <option value="">{p('— non mappé —')}</option>
      {groups.map(g => (
        <optgroup key={g.label} label={g.label}>
          {g.lines.map(l => <option key={l.id} value={l.id}>{l.label}</option>)}
        </optgroup>
      ))}
    </select>
  );
}

export default function MappingPage({ activeTenant, addToast, embedded }) {
  const { t } = useTranslation();
  const p = useP();
  const [accounts, setAccounts]   = useState([]);
  const [lines, setLines]         = useState([]);
  const [loading, setLoading]     = useState(false);
  const [brushMode, setBrushMode] = useState(false);
  const [activeLine, setActiveLine] = useState(null);
  const [editMode, setEditMode]   = useState(false);
  const [selected, setSelected]   = useState([]);
  const [newNum, setNewNum]       = useState('');
  const [newDesc, setNewDesc]     = useState('');
  const numRef = useRef(null);

  function load() {
    if (!activeTenant) { setAccounts([]); setLines([]); return; }
    setLoading(true);
    Promise.all([
      listPlAccounts(activeTenant.id).then(r => setAccounts(r.data || [])),
      listLines(activeTenant.id).then(r => setLines(r.data || [])),
    ])
      .catch(() => addToast(p('Erreur chargement mapping'), 'error'))
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeTenant]);

  async function assign(accountNumber, lineId) {
    const res = await patchPlAccountLine(activeTenant.id, accountNumber, lineId || null);
    if (res.data) {
      setAccounts(prev => prev.map(a =>
        a.account_number === accountNumber ? { ...a, reporting_line: res.data.reporting_line } : a));
    } else {
      addToast(res.error || p('Erreur affectation'), 'error');
    }
  }

  function paint(accountNumber) {
    if (!activeLine) { addToast(p('Choisis d\'abord une reporting line.')); return; }
    assign(accountNumber, activeLine === '__unmap__' ? null : activeLine);
  }

  async function handleAddAccount() {
    const num = newNum.trim();
    if (!num) return;
    const res = await createPlAccount(activeTenant.id, num, newDesc.trim() || null);
    if (res.data) {
      setNewNum('');
      setNewDesc('');
      load();
      numRef.current?.focus();
    } else {
      addToast(res.error || p('Erreur ajout compte'), 'error');
    }
  }

  function handleAddKey(e) {
    if (e.key === 'Enter') handleAddAccount();
  }

  function toggleSelect(accountNumber) {
    setSelected(prev => prev.includes(accountNumber)
      ? prev.filter(n => n !== accountNumber)
      : [...prev, accountNumber]);
  }

  async function handleDeleteSelected() {
    if (!selected.length) return;
    const msg = p('Supprimer {{n}} compte(s) et leurs montants mensuels (toutes années) ?')
      .replace('{{n}}', selected.length);
    if (!window.confirm(msg)) return;
    const res = await deletePlAccounts(activeTenant.id, selected);
    if (res.data) {
      setSelected([]);
      load();
    } else {
      addToast(res.error || p('Erreur suppression'), 'error');
    }
  }

  if (!activeTenant) {
    return <main className="page-table-layout"><div className="page-table-empty">{p('Sélectionne une entreprise.')}</div></main>;
  }

  const groups = [];
  lines.forEach(l => {
    const last = groups[groups.length - 1];
    if (last && last.label === l.section_label) last.lines.push(l);
    else groups.push({ label: l.section_label, lines: [l] });
  });

  const labelOf = id => id === '__unmap__' ? p('Retirer (non mappé)') : (lines.find(l => l.id === id)?.label || '—');
  const unmapped = accounts.filter(a => !a.reporting_line).length;

  return (
    <main className={`page-table-layout is-fluid${embedded ? ' is-embedded' : ''}`}>
      {!embedded && <PageHeader title={p('Mapping comptes')} />}
      <CardShell
        className="report-card"
        title={p('Comptes → lignes de reporting')}
        primaryAction={(
          <>
            <button
              className={`p-btn ${editMode ? 'p-btn--primary' : 'p-btn--brush-off'}`}
              onClick={() => { setEditMode(v => !v); setSelected([]); setBrushMode(false); setActiveLine(null); }}
            >
              {editMode ? p('✓ Terminer') : p('✎ Éditer')}
            </button>
            <button
              className={`p-btn ${brushMode ? 'p-btn--primary' : 'p-btn--brush-off'}`}
              onClick={() => { setBrushMode(v => !v); setActiveLine(null); setEditMode(false); setSelected([]); }}
            >
              {brushMode ? p('✓ Brush mode') : p('🖌 Brush mode')}
            </button>
            <span className={`mx-count${unmapped ? ' mx-count--warn' : ''}`} data-tour="pl-mapping-count">
              {unmapped ? p('{{n}} compte(s) à mapper').replace('{{n}}', unmapped) : p('Tous les comptes sont mappés')}
            </span>
          </>
        )}
      >

      {brushMode && (
        <div className="mx-brushbar">
          <div className="mx-brush-hint">
            {activeLine
              ? <>{p('Pinceau :')} <b>{labelOf(activeLine)}</b> {p('— clique les comptes pour les affecter.')}</>
              : <>{p('Choisis une reporting line :')}</>}
          </div>
          <div className="mx-palette">
            {groups.map(g => (
              <div key={g.label} className="mx-pal-group">
                <span className="mx-pal-sec">{g.label}</span>
                {g.lines.map(l => (
                  <button
                    key={l.id}
                    className={`mx-chip${activeLine === l.id ? ' is-active' : ''}`}
                    onClick={() => setActiveLine(l.id)}
                  >{l.label}</button>
                ))}
              </div>
            ))}
            <button
              className={`mx-chip mx-chip--unmap${activeLine === '__unmap__' ? ' is-active' : ''}`}
              onClick={() => setActiveLine('__unmap__')}
            >{p('Retirer l\'affectation')}</button>
          </div>
        </div>
      )}

      {editMode && (
        <div className="mx-editbar">
          <div className="mx-edit-count">
            {p('{{n}} compte(s) sélectionné(s)').replace('{{n}}', selected.length)}
          </div>
          <div className="mx-edit-actions">
            <button
              className="mx-chip"
              onClick={() => setSelected(accounts.filter(a => !a.reporting_line).map(a => a.account_number))}
            >{p('Sélectionner les non mappés')}</button>
            <button
              className="mx-chip"
              onClick={() => setSelected([])}
              disabled={!selected.length}
            >{p('Tout désélectionner')}</button>
            <button
              className="mx-del-btn"
              onClick={handleDeleteSelected}
              disabled={!selected.length}
            >{p('Supprimer')}</button>
          </div>
        </div>
      )}

      {loading && <p className="page-table-loading">{p('Chargement…')}</p>}

      {!loading && (
        <div className="page-table-wrap">
          <table className="page-table mx-list">
            <thead>
              <tr>
                {editMode && (
                  <th className="mx-col-check">
                    <input
                      type="checkbox"
                      checked={accounts.length > 0 && selected.length === accounts.length}
                      onChange={e => setSelected(e.target.checked ? accounts.map(a => a.account_number) : [])}
                    />
                  </th>
                )}
                <th className="mx-col-num">{p('N° compte')}</th>
                <th>{t('phrases:p_63a9062c64')}</th>
                <th className="mx-col-line">{p('Ligne de reporting')}</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map(a => (
                <tr
                  key={a.account_number}
                  className={`${a.reporting_line ? '' : 'mx-row-unmapped'}${brushMode ? ' mx-paintable' : ''}${editMode ? ' mx-selectable' : ''}${selected.includes(a.account_number) ? ' mx-row-selected' : ''}`}
                  onClick={
                    brushMode ? () => paint(a.account_number)
                      : editMode ? () => toggleSelect(a.account_number)
                        : undefined
                  }
                >
                  {editMode && (
                    <td className="mx-col-check">
                      <input
                        type="checkbox"
                        checked={selected.includes(a.account_number)}
                        onChange={() => toggleSelect(a.account_number)}
                        onClick={e => e.stopPropagation()}
                      />
                    </td>
                  )}
                  <td className="pl-mono">{a.account_number}</td>
                  <td>{a.account_desc}</td>
                  <td>
                    <LineSelect groups={groups} value={a.reporting_line || ''} onChange={code => assign(a.account_number, code)} />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="mx-add-row">
                {editMode && <td className="mx-col-check" />}
                <td>
                  <input
                    ref={numRef}
                    className="mx-add-input mx-add-input--num"
                    placeholder={p('N° compte')}
                    value={newNum}
                    onChange={e => setNewNum(e.target.value)}
                    onKeyDown={handleAddKey}
                  />
                </td>
                <td>
                  <input
                    className="mx-add-input"
                    placeholder={t('phrases:p_bd1ca05924')}
                    value={newDesc}
                    onChange={e => setNewDesc(e.target.value)}
                    onKeyDown={handleAddKey}
                  />
                </td>
                <td>
                  <button
                    className="mx-add-btn"
                    onClick={handleAddAccount}
                    disabled={!newNum.trim()}
                  >{p('+ Ajouter')}</button>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      </CardShell>
    </main>
  );
}
